import React, { ReactElement } from 'react';

import { EmailTask, ProjectType, TeamPermission, useProjectTasksService } from 'nekst-api';
import {
  AttachmentsWidget,
  EmailProjectTaskSmartTagsWysiwyg,
  EmailTitleSmartTagsInput,
  ProjectParentTaskRelationInput,
  SendEmailButton,
  SendFromUserInput
} from 'features/tasks-forms';
import FormTemplate, { AfterSubmitFunc } from '../../../../../shared/uibuilder/form/FormTemplate';
import Row3x3x6x from '../../../../../shared/web/uitheme/form/Row3x3x6x';
import Checkbox from '../../../../../shared/uibuilder/form/input/Checkbox';
import RelativeRow, { EmptyCell } from '../../../../../shared/web/uitheme/form/RelativeRow';
import Separator from '../../../../../shared/web/uitheme/form/Separator';
import EmailTaskAssignmentsInput from './EmailTaskAssignmentsInput';
import TaskCommentsListInput from '../comments/TaskCommentsListInput';
import Right from '../../../../../shared/web/Right';
import OverflowHidden from '../../../../../shared/widgets/OverflowHidden';
import ProjectDropdown from '../ProjectDropdown';
import SendDate from './SendDate';
import { IfWeekendDropdown, ProjectIdContext } from 'features/nekst-widgets';
import { useAuthorizationChecker } from 'authorization-scope';
import useFeatureToggleService, { SendDocumentsAsAttachmentsToggleWrapper, ToggledFeature } from 'feature-toggle';
import { CheckboxInput, FormRows, IfFormData, SimpleBlock } from 'ui-builder';
import styles from './Email.module.scss';

export interface BaseEmailTaskFormProps {
  submitFormFunc: (data: EmailTask) => Promise<EmailTask>;
  afterSubmitFunc?: AfterSubmitFunc,
  data?: EmailTask;
  title: string | ReactElement;
  buttons: ReactElement
  initialData?: EmailTask,
  isDisabled?: boolean | ((data: EmailTask) => boolean)
  showProjectDropdown?: boolean,
  children?: ReactElement
  // eslint-disable-next-line react/no-unused-prop-types
  projectType?: ProjectType,
}

function CheckboxesRow(props: {
  initialData?: EmailTask
}) {
  const { isGranted } = useAuthorizationChecker();

  if (isGranted(TeamPermission.FEATURE_BUSINESS_DAYS) && props.initialData?.parentTaskRelation) {
    return (
      <RelativeRow weights={[2.5, 2.5, 3, 4]}>
        <Checkbox source="isKeyDate" label="Key Date" />
        <Checkbox source="isPrivate" label="Private" />
        <Checkbox source="useBusinessDaysCalculation" label="Business Days" />
        <Checkbox source="isAutoSent" label="Autosend" />
      </RelativeRow>
    );
  } else {
    return (
      <Row3x3x6x>
        <Checkbox source="isKeyDate" label="Key Date" />
        <Checkbox source="isPrivate" label="Private" />
        <Checkbox source="isAutoSent" label="Autosend" />
      </Row3x3x6x>
    );
  }
}

function DependentTasksDueDateRow() {
  const featureToggleService = useFeatureToggleService();

  if (featureToggleService.isFeatureEnabled(ToggledFeature.BUSINESS_DAYS)) {
    return (
      <IfFormData conditionFunc={(data) => !!data.parentTaskRelation}>
        <FormRows>
          <RelativeRow
            weights={[8, 4]}
            useFlex
            toBottom
          >
            <ProjectParentTaskRelationInput source="parentTaskRelation" label="Send" />
            <RelativeRow weights={[1, 11]}>
              <EmptyCell />
              <IfWeekendDropdown />
            </RelativeRow>
          </RelativeRow>
          <RelativeRow weights={[4, 2, 6]}>
            <SendFromUserInput />
          </RelativeRow>
        </FormRows>
      </IfFormData>
    );
  } else {
    return (
      <IfFormData conditionFunc={(data) => !!data.parentTaskRelation}>
        <RelativeRow
          weights={[8, 4]}
          useFlex
          toBottom
        >
          <ProjectParentTaskRelationInput source="parentTaskRelation" label="Send" />
          <RelativeRow weights={[1, 11]}>
            <EmptyCell />
            <SendFromUserInput />
          </RelativeRow>
        </RelativeRow>
      </IfFormData>
    );
  }

}

export default function BaseEmailTaskForm(props: BaseEmailTaskFormProps) {

  const projectTasksService = useProjectTasksService();

  const isDisabled = props.isDisabled ?? false;

  const showProjectDropdown = props.showProjectDropdown || false;

  return (
    <ProjectIdContext projectId={props.initialData?.projectId || 0}>
      <FormTemplate<EmailTask>
        title={props.title}
        submitFormFunc={props.submitFormFunc}
        afterSubmitFunc={props.afterSubmitFunc}
        buttons={props.buttons}
        isDisabled={isDisabled}
        initialData={props.initialData || props.data}
        validationSchema={projectTasksService.getEmailTaskValidationSchema()}
      >
        {showProjectDropdown && (<ProjectDropdown source="projectId" />)}

        <SimpleBlock>
          <EmailTitleSmartTagsInput
            source="name"
            label="Task Name / Email Subject Line"
            afterLabelElement={(
              <Checkbox
                source="addTransactionNameAtEnd"
                label="Add Transaction Name at End"
                className={styles.addNameInput}
              />
            )}
          />
        </SimpleBlock>

        <CheckboxesRow initialData={props.initialData} />
        <RelativeRow
          weights={[7, 5]}
          isVisible={(data) => !data.parentTaskRelation}
        >
          <RelativeRow weights={[5, 7]}>
            <SendDate />
            <SendFromUserInput />
          </RelativeRow>
        </RelativeRow>
        <DependentTasksDueDateRow />
        <Separator />
        <EmailProjectTaskSmartTagsWysiwyg
          source="description"
          label="Email message"
          projectId={(data) => data.projectId}
          projectType={props.data?._project.type || props.projectType}
        />
        <SendDocumentsAsAttachmentsToggleWrapper>
          <AttachmentsWidget />
          <CheckboxInput
            source="isSendDocumentsAsAttachments"
            label="Send Any Linked Documents also as Attachments"
            hint="If the attached documents exceed 25MB, they will be shared as downloadable links instead."
          />
        </SendDocumentsAsAttachmentsToggleWrapper>
        <OverflowHidden>
          <Right isVisible={(data) => data.id}>
            <SendEmailButton addPreviewPrefix />
          </Right>
        </OverflowHidden>
        <Separator />
        <EmailTaskAssignmentsInput />
        <Separator isVisible={(data) => data.id} />
        <TaskCommentsListInput isVisible={(data) => data.id} />
        {props.children || null}
      </FormTemplate>
    </ProjectIdContext>
  );
}
